// BranchListPage.jsx — lọc server-side + mini dashboard
import React, { useCallback, useEffect, useState } from "react";
import {
  Building2,
  Layers,
  Mail,
  MapPin,
  Phone,
  Sparkles,
  User,
} from "lucide-react";
import { useDebounce } from "use-debounce";
import Filter from "./BranchFilter";
import BranchMiniDashboard from "./BranchMiniDashboard";
import RowActions from "./BranchRowAction";
import Pagination from "../../../components/common/Pagination";
import CreateDrawer from "./BranchCreateModal";
import BranchDetailModal from "./BranchDetailModal";
import BranchEditModal from "./BranchEditModal";
import BranchDepartmentsModal from "./BranchDepartmentsModal";
import { getAllBranches, getBranchStatsSummary } from "../../../api/hrm/branches/info";
import { startBranchListTour } from "./branchListTour";
import { hasPermission } from "../../../utils/permissionUtils";

const initialFilters = {
  keyword: "",
  affiliation_type: "",
  is_active: "",
  dashboard: "",
};

const AFFILIATION_LABEL = {
  hq: "Trụ sở / HQ",
  affiliated: "Trực thuộc",
  franchise: "Nhượng quyền",
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const monthShort = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day}-${monthShort}-${year}`;
};

const StatusBadge = ({ isActive }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-widest shadow-sm ${
      isActive
        ? "border-emerald-100 bg-emerald-50 text-emerald-600"
        : "border-slate-200 bg-slate-100 text-slate-400"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        isActive ? "animate-pulse bg-emerald-500" : "bg-slate-300"
      }`}
    />
    {isActive ? "Hoạt động" : "Ngừng nhận"}
  </span>
);

const AffiliationBadge = ({ type }) => {
  if (!type) return null;
  const tone =
    type === "hq"
      ? "border-red-100 bg-red-50 text-red-700"
      : type === "franchise"
        ? "border-amber-100 bg-amber-50 text-amber-700"
        : "border-slate-200 bg-slate-50 text-slate-600";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${tone}`}
    >
      {AFFILIATION_LABEL[type] || type}
    </span>
  );
};

const BranchListPage = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [debouncedKeyword] = useDebounce(filters.keyword, 400);
  const [stats, setStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [detailBranch, setDetailBranch] = useState(null);
  const [editBranch, setEditBranch] = useState(null);
  const [departmentBranch, setDepartmentBranch] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const canManageDepartments = hasPermission("hrm.branches", "R");

  const loadStats = useCallback(async () => {
    try {
      const s = await getBranchStatsSummary();
      setStats(s);
    } catch {
      setStats(null);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const data = await getAllBranches({
        keyword: debouncedKeyword?.trim() || undefined,
        affiliation_type: filters.affiliation_type || undefined,
        is_active: filters.is_active || undefined,
        dashboard: filters.dashboard || undefined,
        page: currentPage,
        limit: pageSize,
      });
      setBranches(data.items || []);
      setTotalRecords(Number(data.total ?? 0));
    } catch (err) {
      console.error("Lỗi khi lấy danh sách chi nhánh:", err);
      setBranches([]);
      setTotalRecords(0);
    }
  }, [
    debouncedKeyword,
    filters.affiliation_type,
    filters.is_active,
    filters.dashboard,
    currentPage,
    pageSize,
  ]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleFilterChange = useCallback((next) => {
    setFilters(next);
    setCurrentPage(1);
  }, []);

  const handleDashboardSelect = useCallback((key) => {
    setFilters((prev) => ({
      ...prev,
      dashboard: key,
      affiliation_type: "",
    }));
    setCurrentPage(1);
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const refreshAll = useCallback(() => {
    loadStats();
    fetchBranches();
  }, [loadStats, fetchBranches]);

  return (
    <div className="w-full min-h-screen bg-gray-50/30 p-3 pb-24 md:p-8 md:pb-8">
      <div
        className="mb-4 flex items-start justify-between gap-3 sm:mb-6 sm:gap-4"
        data-tour="hrm-branches-header"
      >
        <div className="mb-1 flex min-w-0 items-center gap-3 sm:mb-2 sm:gap-4">
          <div className="shrink-0 rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
            <Building2 className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
              Quản lý <span className="text-red-700">chi nhánh</span>
            </h1>
            <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
              Trụ sở, trực thuộc và nhượng quyền — theo dõi hợp đồng NQ theo năm
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => window.setTimeout(() => startBranchListTour(), 80)}
          title="Hướng dẫn sử dụng"
          aria-label="Hướng dẫn sử dụng"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-red-200 bg-red-50/90 text-red-700 shadow-sm transition hover:border-red-300 hover:bg-red-100 sm:h-auto sm:w-auto sm:gap-2 sm:px-4 sm:py-2.5 sm:text-xs sm:font-bold sm:uppercase sm:tracking-wide sm:text-red-800"
        >
          <Sparkles className="h-4 w-4 text-red-600 sm:h-4 sm:w-4" strokeWidth={2.25} />
          <span className="hidden sm:inline">Hướng dẫn sử dụng</span>
        </button>
      </div>

      <div data-tour="hrm-branches-dashboard">
        <BranchMiniDashboard
          stats={stats}
          activeKey={filters.dashboard}
          onSelect={handleDashboardSelect}
        />
      </div>

      <div data-tour="hrm-branches-filter">
        <Filter
          filters={filters}
          onChange={handleFilterChange}
          onNewClick={() => setShowDrawer(true)}
          onRefresh={refreshAll}
        />
      </div>

      <div data-tour="hrm-branches-table">
        {/* Mobile cards */}
        <div className="space-y-3 md:hidden">
          {branches.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white py-14 text-center text-sm font-bold text-gray-400">
              Không có chi nhánh phù hợp bộ lọc.
            </div>
          ) : (
            branches.map((branch) => {
              const isActive = branch.is_active;
              const managerInitial = branch.manager
                ? branch.manager.trim().split(" ").pop().charAt(0)
                : "?";
              return (
                <div
                  key={branch.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setDetailBranch(branch)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDetailBranch(branch);
                    }
                  }}
                  className="w-full cursor-pointer rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition active:scale-[0.99] active:bg-red-50/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-black leading-snug text-slate-800">
                        {branch.name}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <StatusBadge isActive={isActive} />
                        <AffiliationBadge type={branch.affiliation_type} />
                      </div>
                    </div>
                    {canManageDepartments ? (
                      <button
                        type="button"
                        title="Quản lý bộ phận"
                        aria-label="Quản lý bộ phận"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDepartmentBranch(branch);
                        }}
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-600"
                      >
                        <Layers size={18} strokeWidth={2.25} />
                      </button>
                    ) : null}
                  </div>

                  {branch.address ? (
                    <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-500">
                      <MapPin
                        size={13}
                        className="mt-0.5 shrink-0 text-slate-400"
                      />
                      <span className="line-clamp-2">{branch.address}</span>
                    </p>
                  ) : null}

                  <div className="mt-3 space-y-1.5 border-t border-gray-50 pt-3">
                    {branch.email ? (
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                        <Mail size={12} className="shrink-0 text-sky-500" />
                        <span className="truncate">{branch.email}</span>
                      </p>
                    ) : null}
                    {branch.phone ? (
                      <p className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                        <Phone size={12} className="shrink-0 text-slate-400" />
                        {branch.phone}
                      </p>
                    ) : null}
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-[10px] font-black uppercase text-red-600">
                        {managerInitial}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-xs font-bold text-slate-700">
                          {branch.manager || "—"}
                        </p>
                        {branch.manager_phone ? (
                          <p className="truncate text-[10px] font-medium text-slate-400">
                            {branch.manager_phone}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <span className="shrink-0 rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-500">
                      {formatDate(branch.last_changed)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden overflow-hidden rounded-[2.5rem] border border-gray-100 bg-white shadow-xl shadow-slate-200/50 md:block">
        <table className="min-w-full border-separate border-spacing-0">
          <thead className="bg-slate-50/80 backdrop-blur-sm">
            <tr>
              <th className="w-[60px] border-b border-gray-100 p-5 text-center text-[11px] font-black uppercase tracking-widest text-slate-400">
                #
              </th>
              <th className="min-w-[250px] border-b border-gray-100 p-5 text-left text-[11px] font-black uppercase tracking-widest text-slate-400">
                Thông tin chi nhánh
              </th>
              <th className="min-w-[200px] border-b border-gray-100 p-5 text-left text-[11px] font-black uppercase tracking-widest text-slate-400">
                Liên hệ
              </th>
              <th className="min-w-[220px] border-b border-gray-100 p-5 text-left text-[11px] font-black uppercase tracking-widest text-slate-400">
                Đại diện
              </th>
              <th className="min-w-[120px] border-b border-gray-100 p-5 text-center text-[11px] font-black uppercase tracking-widest text-slate-400">
                Cập nhật
              </th>
              <th className="min-w-[130px] border-b border-gray-100 p-5 text-center text-[11px] font-black uppercase tracking-widest text-slate-400">
                Trạng thái
              </th>
              <th className="w-[100px] border-b border-gray-100 p-5 text-center text-[11px] font-black uppercase tracking-widest text-slate-400">
                Thao tác
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50">
            {branches.map((branch, index) => {
              const isActive = branch.is_active;

              return (
                <tr
                  key={branch.id}
                  role="button"
                  tabIndex={0}
                  title="Xem chi tiết chi nhánh"
                  onClick={() => setDetailBranch(branch)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDetailBranch(branch);
                    }
                  }}
                  className="group cursor-pointer transition-all duration-300 hover:bg-red-50/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-200"
                >
                  <td className="p-5 text-center text-xs font-black text-slate-300">
                    {(index + 1).toString().padStart(2, "0")}
                  </td>

                  <td className="p-5">
                    <div className="flex flex-col">
                      <span className="mb-1 text-sm font-black leading-tight text-slate-800 transition-colors group-hover:text-red-700">
                        {branch.name}
                      </span>
                      <div className="flex items-start gap-1 text-[11px] font-medium text-slate-400">
                        <MapPin size={12} className="mt-0.5 shrink-0" />
                        <span className="line-clamp-1 italic">{branch.address}</span>
                      </div>
                      {branch.affiliation_type ? (
                        <div className="mt-1.5">
                          <AffiliationBadge type={branch.affiliation_type} />
                        </div>
                      ) : null}
                    </div>
                  </td>

                  <td className="p-5">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                        {branch.email}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        <div className="h-1.5 w-1.5 rounded-full bg-slate-200" />
                        {branch.phone}
                      </div>
                    </div>
                  </td>

                  <td className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-red-100 bg-red-50 text-[11px] font-black uppercase text-red-600 shadow-sm">
                        {branch.manager
                          ? branch.manager.trim().split(" ").pop().charAt(0)
                          : "?"}
                      </div>
                      <div className="min-w-0">
                        <span className="truncate text-sm font-black text-slate-700">
                          {branch.manager}
                        </span>
                        <span className="block text-[10px] font-bold uppercase tracking-tight text-slate-400 italic">
                          <User size={10} className="mr-1 inline" />
                          {branch.manager_phone}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td className="p-5 text-center">
                    <span className="whitespace-nowrap rounded-lg bg-slate-100 px-3 py-1.5 text-[10px] font-black tracking-tight text-slate-600">
                      {formatDate(branch.last_changed)}
                    </span>
                  </td>

                  <td className="p-5 text-center">
                    <StatusBadge isActive={isActive} />
                  </td>

                  <td
                    className="p-5 text-center"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <div className="flex justify-center">
                      <RowActions
                        branch={branch}
                        onOpenDepartments={(b) => setDepartmentBranch(b)}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
          {branches.length === 0 ? (
            <div className="py-16 text-center text-sm font-bold text-gray-400">
              Không có chi nhánh phù hợp bộ lọc.
            </div>
          ) : null}
        </div>
      </div>

      <Pagination
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <CreateDrawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        onSaved={() => {
          refreshAll();
          setShowDrawer(false);
        }}
      />

      <BranchDetailModal
        isOpen={!!detailBranch}
        branch={detailBranch}
        onClose={() => setDetailBranch(null)}
        onEdit={(b) => {
          setDetailBranch(null);
          setEditBranch(b);
        }}
      />

      <BranchEditModal
        isOpen={!!editBranch}
        branch={editBranch}
        onClose={() => setEditBranch(null)}
        onSaved={() => {
          refreshAll();
          setEditBranch(null);
        }}
      />

      <BranchDepartmentsModal
        branch={departmentBranch}
        isOpen={!!departmentBranch}
        onClose={() => setDepartmentBranch(null)}
      />
    </div>
  );
};

export default BranchListPage;
